import { cloneDeep } from "lodash";
import React, { useContext, useState } from "react";
import { useHouseContext } from "../../HouseContext";
import { DBUser, fullHouseText, House, ReservationStatus, Room, RoomReservation, UserRole } from "../../ostertalertypes";


interface IStepperContextProps {
  activeBooking: RoomReservation[];
  selectedUser: DBUser | null;
  userType: UserRole;
  setUserType: React.Dispatch<React.SetStateAction<UserRole>>;
  setSelectedUser: (user : DBUser | null) => void;
  selectedHouse: House | undefined;
  resetActiveBooking: () => void;
  updateRoom: (newRoomReservation: RoomReservation, index: number) => void;
  addRoom: (room: Room) => void;
  removeRoom: () => void;
  updateSelectedHouse: (house: House) => void;
  getNextAvailableRoomName: () => Room | null;
  filterAvailableRooms: (selectedRoom: string) => Room[];
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  isFullHouse: () => boolean;
}


const defaultContextState = {
  activeBooking: [],
  selectedUser: null,
  userType: UserRole.NORMAL_ACCOUNT,
  setUserType: (usertype: React.SetStateAction<UserRole>) => {},
  setSelectedUser: (user: DBUser | null) => {},
  selectedHouse: undefined,
  resetActiveBooking: ():void => {},
  updateRoom: (newRoomReservation: RoomReservation, index: number): void => {},
  addRoom: (room: Room): void => {},
  removeRoom: ():void => {},
  updateSelectedHouse: (house: House):void => {},
  getNextAvailableRoomName: ():(Room | null) => {return null},
  filterAvailableRooms: (selectedRoom: string): Room[] => { return []},
  dialogOpen: false,
  setDialogOpen: (open: React.SetStateAction<boolean>) => {},
  activeStep: 0,
  setActiveStep: (step: React.SetStateAction<number>) => {},
  isFullHouse: (): boolean => { return false; }
};

const StepperContext = React.createContext<IStepperContextProps>(defaultContextState);
// Define our own custom convinent hook
export const useStepperContext = () => useContext(StepperContext);



export const StepperProvider: React.FC = (props) => {
  const houseCtx = useHouseContext();
  const [activeBooking, setActiveBooking] = useState<RoomReservation[]>([]);
  const [selectedUserState, setSelectedUserState] = useState<DBUser | null>(null);
  const [selectedHouse, setSelectedHouse] = useState<House | undefined>(houseCtx.houses.values().next().value);
  const [userType, setUserType] = useState<UserRole>(UserRole.NORMAL_ACCOUNT);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const defaultBooking= { houseName: "", roomName: "", status: ReservationStatus.BOOKING_REQUESTED, fullHouse: false, guestCount: 0 }

  const isFullHouse = () => {
    return activeBooking.length === 1 && activeBooking[0].fullHouse;
  }

  const resetActiveBooking = () => {
    const house = houseCtx.houses.values().next().value;
    const r: Room =  house.rooms.values().next().value;
    setUserType(UserRole.NORMAL_ACCOUNT);
    setSelectedHouse(house);
    setActiveStep(0);
    setActiveBooking([{...defaultBooking, houseName: house.houseName, roomName: r.roomName}]);
  }

  const updateSelectedHouse = (house: House) => {
    setSelectedHouse(house)
    const r: Room =  house.rooms.values().next().value;
    setActiveBooking([{...defaultBooking, houseName: house.houseName, roomName: r.roomName}]);
  }

  const updateRoom = (newRoomReservation: RoomReservation, index: number) => {
    let temp = cloneDeep(activeBooking);
    if (newRoomReservation.fullHouse) {
      // set complete house as the only booking
      setActiveBooking([newRoomReservation])
    } else {
      // inject the booking
      temp.splice(index, 1, newRoomReservation);
      setActiveBooking(temp);
    }
  };

  const addRoom = (room: Room) => {
    let tmp = cloneDeep(activeBooking);
    if (selectedHouse && defaultBooking) {
      tmp.push({ ...defaultBooking, houseName: selectedHouse.houseName, roomName: room.roomName });
    }
    setActiveBooking(tmp);
  };

  const removeRoom = () => {
    let tmp = cloneDeep(activeBooking);
    tmp.pop();
    setActiveBooking(tmp);
  };

  const setSelectedUser = (user: DBUser | null) => {
    setSelectedUserState(user);
  }

 /**
  * Finds out the rooms which do not already belong to the booking.
  * @returns
  */
  const getNextAvailableRoomName = (): Room | null => {
    let availableRooms : Room[] = cloneDeep(Array.from(selectedHouse!.rooms.values()));
    availableRooms = availableRooms.filter(room => room.roomName !== fullHouseText); // Gesamtes Haus shall not come as a next option when users hits plus to add rooms
    let myRoomName: Room | null = null;
    activeBooking.forEach((res, i) => {
        const index = availableRooms.findIndex(r => r.roomName === res.roomName);
        if (index > -1) {
          availableRooms.splice(index, 1);
        }
    })
    if (availableRooms.length > 0) {
      myRoomName = availableRooms[0];
    }
    return myRoomName;
  }

 /**
  * Prevents booking the same room twice in one booking.
  * It checks if the room is already part of the booking and then does not offer
  * it in the dropdown.
  * @param selectedRoom the selected room in the dropdown
  * @returns
  */
  const filterAvailableRooms = (selectedRoom: string): Room[] => {
    let availableRooms : Room[] = cloneDeep(Array.from(selectedHouse!.rooms.values()));
    activeBooking.forEach((res, i) => {
      // the own room name must be always included in the options
      if (res.roomName !== selectedRoom){
        // find out the available rooms => these are the rooms not part of other dropdown selections
        const index = availableRooms.findIndex(r => r.roomName === res.roomName);
        if (index > -1) {
          availableRooms.splice(index, 1);
        }
      }
    })
    return availableRooms;
  }


  const contextValue = {
    activeBooking: activeBooking,
    selectedHouse: selectedHouse,
    selectedUser: selectedUserState,
    setSelectedUser: setSelectedUser,
    userType: userType,
    setUserType: setUserType,
    resetActiveBooking: resetActiveBooking,
    updateRoom: updateRoom,
    addRoom: addRoom,
    removeRoom: removeRoom,
    updateSelectedHouse: updateSelectedHouse,
    getNextAvailableRoomName: getNextAvailableRoomName,
    filterAvailableRooms: filterAvailableRooms,
    dialogOpen: dialogOpen,
    setDialogOpen: setDialogOpen,
    activeStep: activeStep,
    setActiveStep: setActiveStep,
    isFullHouse: isFullHouse
  };

  return <StepperContext.Provider value={contextValue}>{props.children}</StepperContext.Provider>;
};
